/**
 * Build the `src` / `srcset` / `sizes` an `<img>` needs to go through the
 * ArchiPro CDN loader, and the matching `<link rel="preload">` descriptor.
 *
 * Both sides come out of the same function on the same {@link imageConfig}, so
 * a preloaded hero and the image the browser ends up rendering resolve to the
 * same URL. Building them separately is what leaves a "preloaded but not used"
 * warning and downloads the hero twice.
 */
import type { MetaDescriptor } from 'react-router';
import { getWidths } from '@rocco/components/image/utils/get-image-props';
import { imageConfig } from '../config';

export interface ImageAttrsOptions {
    /**
     * The rendered `sizes` attribute, e.g. from `getImagesSizes()`. Drives which
     * srcset candidates are generated, so pass the same string to the image and
     * its preload.
     */
    sizes?: string;
    /** Fixed rendered width in px. Use instead of `sizes` for images that are not viewport-relative. */
    width?: number;
    quality?: number;
}

export interface ImageAttrs {
    /** Undefined for absolute URLs, which are served untransformed. */
    srcSet: string | undefined;
    /** Undefined for fixed-width images, which use `x` descriptors instead. */
    sizes: string | undefined;
    src: string;
}

// `getWidths` selects candidates against the full size list, not just the
// device widths, so mirror the shape the Rocco Image component builds.
const config = {
    ...imageConfig,
    allSizes: [
        ...new Set([...imageConfig.deviceSizes, ...imageConfig.imageSizes]),
    ].sort((a, b) => a - b),
};

/** The loader-backed `<img>` attributes for a CMS image path. */
export const getImageAttrs = (
    src: string,
    { sizes, width, quality }: ImageAttrsOptions = {},
): ImageAttrs => {
    // An absolute URL is already a finished address — the CDN loader would nest
    // it under /images/cdn-images/ and break it. Served as-is, like the Rocco
    // Image component does.
    if (src.startsWith('https://') || src.startsWith('http://')) {
        return { srcSet: undefined, sizes: undefined, src };
    }

    const { widths, kind } = getWidths(config, width, sizes);
    const { loader } = imageConfig;

    // `src` is deliberately last. React sets attributes in the order they are
    // spread, and Safari starts fetching the moment it sees `src` — before
    // `srcset` and `sizes` land, which costs a second, wrongly-sized download.
    // Rocco's own `generateImgAttrs` orders it the same way for this reason.
    return {
        srcSet: widths
            .map(
                (candidate, index) =>
                    `${loader({ src, width: candidate, quality })} ${
                        kind === 'w' ? candidate : index + 1
                    }${kind}`,
            )
            .join(', '),
        sizes: kind === 'w' ? (sizes ?? '100vw') : undefined,
        src: loader({
            src,
            width: widths[widths.length - 1] ?? 0,
            quality,
        }),
    };
};

/**
 * The `<link rel="preload">` for an image rendered with the same options.
 * Returns an empty list when there is no image, so callers can spread it
 * unconditionally.
 */
export const getImagePreloadLink = (
    src: string | undefined,
    options: ImageAttrsOptions = {},
): MetaDescriptor[] => {
    if (!src) return [];

    const attrs = getImageAttrs(src, options);

    return [
        {
            tagName: 'link',
            rel: 'preload',
            as: 'image',
            fetchPriority: 'high',
            href: attrs.src,
            // An absolute URL has no generated candidates, so the preload is
            // the plain href — emitting an empty imagesrcset would stop the
            // browser resolving it at all.
            ...(attrs.srcSet ? { imageSrcSet: attrs.srcSet } : {}),
            ...(attrs.sizes ? { imageSizes: attrs.sizes } : {}),
        },
    ];
};
